import React, { useState, useEffect } from 'react';
import {
  X, Loader2, CheckCircle2, AlertCircle,
  Layers, Users, GraduationCap, Zap, BookOpen, ArrowRight
} from 'lucide-react';
import {
  AcademicYear, AcademicYearTransition, PromotionMapping, OperationalClass,
  getTransition, generateClasses, getPromotionPreview, promoteStudents, activateTransition
} from '@/actions/data-fetch-academic-years';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  year: AcademicYear;
  onClose: () => void;
  onActivated: () => void;
}

// ─── Step indicator ───────────────────────────────────────────────────────────

const STEPS = ['Generate Classes', 'Plan Promotions', 'Activate Year'];

const stepFromStatus = (status: AcademicYearTransition['status']): number => {
  if (status === 'draft') return 0;
  if (status === 'classes_generated') return 1;
  if (status === 'promotions_mapped') return 2;
  return 3; // completed
};

// ─── Main wizard ─────────────────────────────────────────────────────────────

const AcademicYearWizard: React.FC<Props> = ({ year, onClose, onActivated }) => {
  const [transition, setTransition] = useState<AcademicYearTransition | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Promotion step state
  const [preview, setPreview] = useState<{ is_first_year: boolean; mappings: PromotionMapping[]; to_classes: OperationalClass[] } | null>(null);
  const [editedMappings, setEditedMappings] = useState<{ from_class_id: number; to_class_id: number | null }[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => { fetchTransition(); }, []);

  const fetchTransition = async () => {
    setLoading(true);
    setError(null);
    try {
      const t = await getTransition(year.id);
      setTransition(t);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async () => {
    setLoadingPreview(true);
    setError(null);
    try {
      const p = await getPromotionPreview(year.id);
      setPreview(p);
      setEditedMappings(p.mappings.map(m => ({ from_class_id: m.from_class_id, to_class_id: m.suggested_to_class_id })));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    if (transition?.status === 'classes_generated' || transition?.status === 'promotions_mapped') {
      fetchPreview();
    }
  }, [transition?.status]);

  const handleGenerateClasses = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const res = await generateClasses(year.id);
      setSuccess(res.data?.message || 'Classes generated');
      await fetchTransition();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handlePromoteStudents = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const res = await promoteStudents(year.id, editedMappings);
      setSuccess(res.data?.message || 'Promotions recorded');
      await fetchTransition();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleActivate = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      await activateTransition(year.id);
      onActivated();
    } catch (e: any) { setError(e.message); setSaving(false); }
  };

  const currentStep = transition ? stepFromStatus(transition.status) : 0;
  const isCompleted = transition?.status === 'completed';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isCompleted ? 'Year Setup Complete' : `Set Up ${year.year_label}`}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {new Date(year.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              {' – '}
              {new Date(year.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step bar */}
        {!isCompleted && (
          <div className="px-6 pt-5 pb-4 shrink-0">
            <div className="flex items-center gap-2">
              {STEPS.map((label, i) => {
                const done = i < currentStep;
                const active = i === currentStep;
                return (
                  <React.Fragment key={i}>
                    <div className={`flex items-center gap-2 ${i > 0 ? 'flex-1' : ''}`}>
                      {i > 0 && <div className={`h-px flex-1 ${done ? 'bg-brandColor' : 'bg-gray-200 dark:bg-gray-700'}`} />}
                      <div className={`flex items-center gap-1.5 shrink-0`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors
                          ${done ? 'bg-brandColor text-white' : active ? 'bg-brandColor/10 text-brandColor border-2 border-brandColor' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>
                          {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
                        </div>
                        <span className={`text-xs font-medium ${active ? 'text-gray-900 dark:text-white' : done ? 'text-brandColor' : 'text-gray-400'}`}>
                          {label}
                        </span>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brandColor" /></div>
          ) : isCompleted ? (
            <CompletedView transition={transition!} />
          ) : currentStep === 0 ? (
            <GenerateStep transition={transition!} />
          ) : currentStep === 1 ? (
            <PromotionStep
              transition={transition!}
              preview={preview}
              loadingPreview={loadingPreview}
              editedMappings={editedMappings}
              setEditedMappings={setEditedMappings}
            />
          ) : (
            <ActivateStep transition={transition!} year={year} />
          )}

          {error && (
            <div className="mt-4 flex items-start gap-2.5 p-3.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-700 dark:text-red-400">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {error}
            </div>
          )}
          {success && (
            <div className="mt-4 flex items-start gap-2.5 p-3.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-sm text-green-700 dark:text-green-400">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              {success}
            </div>
          )}
        </div>

        {/* Footer */}
        {!isCompleted && (
          <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-3 shrink-0">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
              Close
            </button>
            {currentStep === 0 && (
              <button onClick={handleGenerateClasses} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-brandColor hover:bg-brandColor/90 rounded-xl transition-colors disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
                {saving ? 'Generating…' : transition?.status === 'draft' ? 'Generate Classes' : 'Regenerate Classes'}
              </button>
            )}
            {currentStep === 1 && (
              <button onClick={handlePromoteStudents} disabled={saving || loadingPreview}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-brandColor hover:bg-brandColor/90 rounded-xl transition-colors disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                {saving ? 'Processing…' : 'Confirm Promotions'}
              </button>
            )}
            {currentStep === 2 && (
              <button onClick={handleActivate} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-xl transition-colors disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {saving ? 'Activating…' : `Activate ${year.year_label}`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Step 1 — Generate Classes ────────────────────────────────────────────────

const GenerateStep: React.FC<{ transition: AcademicYearTransition }> = ({ transition }) => (
  <div className="py-4 space-y-4">
    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl">
      <div className="flex items-start gap-3">
        <BookOpen className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">What this does</p>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Reads your curriculum template (classes + sections from Academic Setup) and creates
            enrollable class instances for <strong>{transition.toYear?.year_label}</strong>.
            This step is safe to rerun — existing records are skipped.
          </p>
        </div>
      </div>
    </div>

    {transition.classes_generated > 0 && (
      <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-xl">
        <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
        <div>
          <p className="text-sm font-medium text-green-800 dark:text-green-200">
            {transition.classes_generated} class instance{transition.classes_generated !== 1 ? 's' : ''} generated
          </p>
          <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">Click below to proceed to the next step</p>
        </div>
      </div>
    )}
  </div>
);

// ─── Step 2 — Promotion Plan ──────────────────────────────────────────────────

const PromotionStep: React.FC<{
  transition: AcademicYearTransition;
  preview: { is_first_year: boolean; mappings: PromotionMapping[]; to_classes: OperationalClass[] } | null;
  loadingPreview: boolean;
  editedMappings: { from_class_id: number; to_class_id: number | null }[];
  setEditedMappings: React.Dispatch<React.SetStateAction<{ from_class_id: number; to_class_id: number | null }[]>>;
}> = ({ transition, preview, loadingPreview, editedMappings, setEditedMappings }) => {
  if (loadingPreview) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-brandColor" /></div>;
  if (!preview) return null;

  if (preview.is_first_year) {
    return (
      <div className="py-4">
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl text-sm text-blue-700 dark:text-blue-300">
          This is the first academic year setup — there are no existing students to promote.
          Click <strong>Confirm Promotions</strong> to proceed to activation.
        </div>
      </div>
    );
  }

  const updateMapping = (fromClassId: number, toClassId: number | null) => {
    setEditedMappings(prev => prev.map(m => m.from_class_id === fromClassId ? { ...m, to_class_id: toClassId } : m));
  };

  const totalStudents = preview.mappings.reduce((s, m) => s + m.student_count, 0);
  const graduating = editedMappings.filter(m => m.to_class_id === null).length;

  return (
    <div className="py-2 space-y-4">
      {/* Summary pills */}
      <div className="flex gap-3 flex-wrap">
        <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg text-xs font-medium text-gray-700 dark:text-gray-300">
          {totalStudents} students total
        </div>
        <div className="px-3 py-1.5 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-xs font-medium text-orange-700 dark:text-orange-400">
          {graduating} class{graduating !== 1 ? 'es' : ''} graduating
        </div>
      </div>

      {/* Mapping table */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <div className="grid px-4 py-2.5 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-100 dark:border-gray-800 text-[11px] font-semibold text-gray-400 uppercase tracking-wider"
          style={{ gridTemplateColumns: '1fr 40px 1fr 60px' }}>
          <span>Current Class</span>
          <span></span>
          <span>Moves To</span>
          <span className="text-right">Students</span>
        </div>

        {preview.mappings.map((row) => {
          const edited = editedMappings.find(m => m.from_class_id === row.from_class_id);
          const currentToId = edited !== undefined ? edited.to_class_id : row.suggested_to_class_id;
          const isGraduating = currentToId === null;

          return (
            <div key={row.from_class_id}
              className="grid items-center px-4 py-3 border-b border-gray-50 dark:border-gray-800 last:border-0 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors text-sm"
              style={{ gridTemplateColumns: '1fr 40px 1fr 60px' }}>

              <span className="font-medium text-gray-800 dark:text-gray-200 truncate">{row.from_class_name}</span>

              <ArrowRight className={`w-4 h-4 mx-auto ${isGraduating ? 'text-orange-400' : 'text-gray-300'}`} />

              <select
                value={currentToId ?? ''}
                onChange={e => updateMapping(row.from_class_id, e.target.value === '' ? null : Number(e.target.value))}
                className="w-full text-sm bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 text-gray-700 dark:text-gray-300 focus:outline-none focus:border-brandColor dark:bg-gray-900">
                <option value="">🎓 Graduate</option>
                {preview.to_classes.map(tc => (
                  <option key={tc.id} value={tc.id}>{tc.class_name}</option>
                ))}
              </select>

              <span className="text-right text-gray-500 dark:text-gray-400">{row.student_count}</span>
            </div>
          );
        })}
      </div>

      {transition.students_promoted > 0 && (
        <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Last run: {transition.students_promoted} promoted, {transition.students_graduated} graduated
        </div>
      )}
    </div>
  );
};

// ─── Step 3 — Activate ────────────────────────────────────────────────────────

const ActivateStep: React.FC<{ transition: AcademicYearTransition; year: AcademicYear }> = ({ transition, year }) => (
  <div className="py-4 space-y-4">
    {/* Summary cards */}
    <div className="grid grid-cols-3 gap-3">
      {[
        { label: 'Classes', value: transition.classes_generated, icon: <BookOpen className="w-4 h-4 text-blue-500" />, bg: 'bg-blue-50 dark:bg-blue-900/20' },
        { label: 'Promoted', value: transition.students_promoted, icon: <Users className="w-4 h-4 text-brandColor" />, bg: 'bg-brandColor/5 dark:bg-brandColor/10' },
        { label: 'Graduated', value: transition.students_graduated, icon: <GraduationCap className="w-4 h-4 text-orange-500" />, bg: 'bg-orange-50 dark:bg-orange-900/20' },
      ].map(c => (
        <div key={c.label} className={`${c.bg} rounded-xl p-4 text-center`}>
          <div className="flex justify-center mb-2">{c.icon}</div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{c.value}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{c.label}</p>
        </div>
      ))}
    </div>

    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
      <div className="flex items-start gap-3">
        <Zap className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-1">Ready to activate</p>
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Activating <strong>{year.year_label}</strong> will make it the current academic year for your school.
            The previous year will remain accessible in read-only history.
          </p>
        </div>
      </div>
    </div>
  </div>
);

// ─── Completed view ───────────────────────────────────────────────────────────

const CompletedView: React.FC<{ transition: AcademicYearTransition }> = ({ transition }) => (
  <div className="py-6 text-center space-y-4">
    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
      <CheckCircle2 className="w-8 h-8 text-green-500" />
    </div>
    <div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
        {transition.toYear?.year_label} is active
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
        {transition.completed_at
          ? `Activated on ${new Date(transition.completed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`
          : 'Year successfully activated'}
      </p>
    </div>
    <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
      {[
        { label: 'Classes', value: transition.classes_generated },
        { label: 'Promoted', value: transition.students_promoted },
        { label: 'Graduated', value: transition.students_graduated },
      ].map(c => (
        <div key={c.label} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-gray-900 dark:text-white">{c.value}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{c.label}</p>
        </div>
      ))}
    </div>
  </div>
);

export default AcademicYearWizard;
